// 📄 pages/Admin/sections/ValidatorTemplatesSection.tsx
import { useEffect, useState } from 'react';
import { Plus, RefreshCw, FileCode, Pencil, Trash2 } from 'lucide-react';
import { listarPlantillasGlobales, eliminarPlantillaGlobal, PlantillaGlobal } from '../../../services/plantillaReferenciaService.ts';
import PlantillaGlobalFormModal from '../components/PlantillaGlobalFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

// Colores por documentStage — mismos tokens Tailwind (no hex) que ya usa
// el resto de la app para badges (ej. severidad del Validador de
// Estructura, ver ValidadorEstructura.tsx).
const COLOR_STAGE: Record<string, string> = {
  Inbound: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  PreProcess: 'text-sky-300 bg-sky-500/10 border-sky-500/30',
  Process: 'text-purple-300 bg-purple-500/10 border-purple-500/30',
  Extension: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  Routing: 'text-gray-300 bg-gray-500/10 border-gray-500/30',
  Outbound: 'text-orange-300 bg-orange-500/10 border-orange-500/30'
};
const COLOR_STANDARD = 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30';

export default function ValidatorTemplatesSection() {
  const [plantillas, setPlantillas] = useState<PlantillaGlobal[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<PlantillaGlobal | null>(null);
  const [aEliminar, setAEliminar] = useState<PlantillaGlobal | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await listarPlantillasGlobales();
      setPlantillas(data.sort((a, b) => a.tipoDocumento.localeCompare(b.tipoDocumento)));
    } catch (err) {
      console.error('Error cargando plantillas globales:', err);
      setError('No se pudieron cargar las plantillas globales.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditando(null); setModalOpen(true); };
  const openEdit = (p: PlantillaGlobal) => { setEditando(p); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await eliminarPlantillaGlobal(aEliminar.tipoDocumento);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar la plantilla global:', err);
    } finally {
      setEliminando(false);
    }
  };

  const formatearFecha = (fecha: any): string => {
    if (!fecha) return '';
    const ms = typeof fecha?.toMillis === 'function' ? fecha.toMillis() : (typeof fecha?._seconds === 'number' ? fecha._seconds * 1000 : null);
    if (!ms) return '';
    return new Date(ms).toLocaleDateString();
  };

  const hayMetadatos = (p: PlantillaGlobal): boolean =>
    !!(p.documentStage || p.documentStandard || (p.sistemaOrigen && p.sistemaDestino));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Plantillas del Validador de Estructura</h1>
          <p className="text-sm text-gray-400 mt-1">Plantillas globales de referencia — el estándar por defecto para cualquier proyecto que no tenga la suya propia.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nuevo tipo
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando plantillas...</p>
        </div>
      ) : plantillas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FileCode className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay plantillas globales registradas.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {plantillas.map((p) => (
            <div key={p.tipoDocumento} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-indigo-500/10">
                <FileCode className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold text-indigo-300">{p.nombre || p.tipoDocumento}</span>
                  <span className="text-xs text-gray-500 truncate">{p.nombreArchivo}</span>
                </div>
                {p.nombre && p.nombre !== p.tipoDocumento && (
                  <p className="text-[10px] text-gray-600 font-mono mt-0.5 truncate">ID: {p.tipoDocumento}</p>
                )}
                {hayMetadatos(p) && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    {p.documentStage && (
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${COLOR_STAGE[p.documentStage] || COLOR_STAGE.Routing}`}>
                        {p.documentStage}
                      </span>
                    )}
                    {p.documentStandard && (
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${COLOR_STANDARD}`}>
                        {p.documentStandard}
                      </span>
                    )}
                    {p.sistemaOrigen && p.sistemaDestino && (
                      <span className="text-[11px] text-gray-500">{p.sistemaOrigen} → {p.sistemaDestino}</span>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-500 mt-1">
                  {p.subidoPor && <>Subido por {p.subidoPor}</>}
                  {formatearFecha(p.fecha) && <> · {formatearFecha(p.fecha)}</>}
                </p>
              </div>
              <button
                onClick={() => openEdit(p)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                title="Editar"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setAEliminar(p)}
                className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                title="Eliminar"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <PlantillaGlobalFormModal plantilla={editando} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta plantilla global?"
          message={`El tipo "${aEliminar.tipoDocumento}" dejará de tener una plantilla global. Los proyectos con su propia plantilla de este tipo no se ven afectados. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
